import { Avatar, Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import StickyNote2RoundedIcon from "@mui/icons-material/StickyNote2Rounded";
import FolderCopyRoundedIcon from "@mui/icons-material/FolderCopyRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { timeAgo, initials } from "./workspaceUtils";

export default function TaskCard({ task, onEdit, onDelete }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("editar tarea");
  const canDelete = hasPermission("eliminar tarea");

  const creatorName = task.created_by
    ? `${task.created_by.name ?? ""} ${task.created_by.lastname ?? ""}`.trim()
    : "Usuario";

  const legalFileCount = Array.isArray(task.legal_file_codes)
    ? task.legal_file_codes.length
    : (task.legal_files_count ?? 0);

  const borderCol = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)";

  return (
    <Box
      onClick={() => navigate(`/workspace/${task.id}`)}
      sx={{
        p: 2.2,
        borderRadius: 4,
        cursor: "pointer",
        border: `1px solid ${borderCol}`,
        background: isDark ? "rgba(255,255,255,0.045)" : "rgba(255,255,255,0.9)",
        boxShadow: isDark ? "0 10px 26px rgba(0,0,0,0.22)" : "0 8px 22px rgba(15,23,42,0.05)",
        transition: "all .2s ease",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          transform: "translateY(-3px)",
          borderColor: "rgba(25,118,210,0.35)",
          boxShadow: "0 18px 40px rgba(0,0,0,0.14)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.6 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            borderRadius: 3,
            bgcolor: "primary.main",
            color: "white",
            flexShrink: 0,
            boxShadow: "0 6px 16px rgba(25,118,210,0.28)",
          }}
        >
          <StickyNote2RoundedIcon fontSize="small" />
        </Avatar>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Tooltip title={task.created_at ? new Date(task.created_at).toLocaleString() : ""}>
            <Chip
              size="small"
              label={timeAgo(task.created_at)}
              sx={{
                height: 24,
                borderRadius: 999,
                bgcolor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.045)",
                "& .MuiChip-label": { px: 1.1, fontWeight: 700, fontSize: 11.5, opacity: 0.75 },
              }}
            />
          </Tooltip>

          {canEdit && (
            <Tooltip title="Editar tarea">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(task);
                }}
                sx={{
                  width: 24,
                  height: 24,
                  color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
                  "&:hover": { color: "primary.main", bgcolor: "rgba(25,118,210,0.1)" },
                }}
              >
                <EditRoundedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}

          {canDelete && (
            <Tooltip title="Eliminar tarea">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(task);
                }}
                sx={{
                  width: 24,
                  height: 24,
                  color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
                  "&:hover": { color: "#d91b06", bgcolor: "rgba(245,31,11,0.1)" },
                }}
              >
                <DeleteRoundedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Typography
        sx={{
          fontWeight: 800,
          fontSize: 15.5,
          lineHeight: 1.42,
          flex: 1,
          display: "-webkit-box",
          WebkitLineClamp: 4,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          whiteSpace: "pre-wrap",
          mb: 1.8,
        }}
      >
        {task.text}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pt: 1.4,
          borderTop: `1px solid ${borderCol}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, minWidth: 0 }}>
          <Avatar
            sx={{
              width: 22,
              height: 22,
              fontSize: 10.5,
              fontWeight: 800,
              bgcolor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
              color: isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.65)",
            }}
          >
            {initials(creatorName)}
          </Avatar>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, opacity: 0.75, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {creatorName}
          </Typography>
        </Box>

        {legalFileCount > 0 && (
          <Chip
            size="small"
            icon={<FolderCopyRoundedIcon sx={{ fontSize: "13px !important" }} />}
            label={legalFileCount}
            sx={{
              height: 24,
              borderRadius: 999,
              flexShrink: 0,
              bgcolor: "rgba(25,118,210,0.10)",
              color: "primary.main",
              "& .MuiChip-label": { px: 0.9, fontWeight: 800, fontSize: 12 },
            }}
          />
        )}
      </Box>
    </Box>
  );
}
